import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import httpService from '../../../services/httpService';
import { store } from '../../../redux/store';
import { setSelectedCommunity, setSelectedBusiness } from '../../../redux/slices/applicationSlice';

export default function CommunityRedirectPage(): JSX.Element {
  const { communityId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const invalidNavigate = (): void => {
    toast.error(t('toast.error.general.invalidInvite'));
    navigate('/');
  };

  useEffect(() => {
    if (!communityId) {
      invalidNavigate();
      return;
    }

    // if the user is not authenticated, store the intended path and redirect to login
    const token = localStorage.getItem('token');
    if (!token) {
      localStorage.setItem('redirectAfterLogin', `/c/${communityId}`);
      navigate('/login', { replace: true });
      return;
    }

    // if the route param is a numeric community id, verify & select directly
    const numericId = Number(communityId);
    const isNumericId = Number.isInteger(numericId) && String(numericId) === communityId;

    if (isNumericId) {
      httpService
        .get(`/networks/communities/${numericId}`)
        .then(({ data }) => {
          if (!data) {
            invalidNavigate();
            return;
          }

          // select the community and go to community overview
          store.dispatch(setSelectedCommunity(data.id ?? numericId));
          navigate('/overview', { replace: true });
        })
        .catch((err) => {
          if (axios.isAxiosError(err) && err.response?.status === 401) {
            // token expired or invalid, redirect to login
            localStorage.setItem('redirectAfterLogin', `/c/${communityId}`);
            navigate('/login', { replace: true });
            return;
          }
          console.error('Failed to fetch community by id:', err);
          invalidNavigate();
        });

      return;
    }

    // fallback: treat param as an invite-friendlyId and resolve via invite-links
    httpService
      .get(`/networks/invite-links/${communityId}`)
      .then(({ data }) => {
        if (!data) {
          invalidNavigate();
          return;
        }

        if (data.communityId) {
          // select the community and go to community overview
          store.dispatch(setSelectedCommunity(data.communityId));
          navigate('/overview', { replace: true });
          return;
        }

        if (data.businessId) {
          // select the business and open communities management
          store.dispatch(setSelectedBusiness(data.businessId));
          navigate('/contacts', { replace: true });
          return;
        }

        invalidNavigate();
      })
      .catch((err) => {
        if (axios.isAxiosError(err) && err.response?.status === 401) {
          localStorage.setItem('redirectAfterLogin', `/c/${communityId}`);
          navigate('/login', { replace: true });
          return;
        }
        console.error('Failed to resolve invite link:', err);
        invalidNavigate();
      });
  }, [communityId]);

  return <></>;
}
